import { useState } from 'react';
import { Link } from 'react-router-dom';
import { fmtMoney } from '../lib/site';

/* Every number here is set by the visitor and every formula is shown.
   A calculator a GM cannot audit is a calculator a GM does not believe. */

function Slider({ label, value, min, max, step = 1, format, onChange }) {
  const fill = ((value - min) / (max - min)) * 100;
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
        <label
          htmlFor={`sl-${label.replace(/\W+/g, '-')}`}
          style={{ fontSize: '0.92rem', fontWeight: 600 }}
        >
          {label}
        </label>
        <span className="mono" style={{ fontSize: '1.02rem', fontWeight: 600, color: 'var(--fairway)' }}>
          {format(value)}
        </span>
      </div>
      <input
        id={`sl-${label.replace(/\W+/g, '-')}`}
        type="range"
        className="sl"
        min={min}
        max={max}
        step={step}
        value={value}
        style={{ '--fill': `${fill}%` }}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}

export default function Calculator({ compact = false }) {
  const [model, setModel] = useState('barter');
  const [rounds, setRounds] = useState(150);
  const [fee, setFee] = useState(55);
  const [trade, setTrade] = useState(120);
  const [commission, setCommission] = useState(20);
  const [plan, setPlan] = useState(197);

  const mglRounds = model === 'barter' ? trade : rounds;
  const theirs = model === 'barter' ? trade * fee * 12 : rounds * fee * (commission / 100) * 12;
  const mgl = plan * 12 + 1.8 * mglRounds * 12;
  const delta = theirs - mgl;

  const theirsLine =
    model === 'barter'
      ? `${trade} trade rounds × $${fee} × 12 months = ${fmtMoney(theirs)}`
      : `${rounds} rounds × $${fee} × ${commission}% × 12 months = ${fmtMoney(theirs)}`;
  const mglLine = `$${plan} × 12 months + $1.80 × ${mglRounds} rounds × 12 months = ${fmtMoney(mgl)}`;

  return (
    <div className="g2" style={{ alignItems: 'center' }}>
      <div>
        {!compact && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 26, flexWrap: 'wrap' }} role="group" aria-label="Current platform model">
            {[
              { key: 'barter', label: 'We trade tee times' },
              { key: 'commission', label: 'We pay a commission' },
            ].map((m) => (
              <button
                key={m.key}
                type="button"
                className="btn btn-line btn-sm"
                aria-pressed={model === m.key}
                style={model === m.key ? { background: 'var(--pine)', color: 'var(--cream)', borderColor: 'var(--pine)' } : undefined}
                onClick={() => setModel(m.key)}
              >
                {m.label}
              </button>
            ))}
          </div>
        )}

        {model === 'barter' ? (
          <Slider
            label="Rounds you trade away each month"
            value={trade}
            min={0}
            max={240}
            step={4}
            format={(v) => `${v}`}
            onChange={setTrade}
          />
        ) : (
          <>
            <Slider
              label="Rounds booked through the platform each month"
              value={rounds}
              min={0}
              max={500}
              step={10}
              format={(v) => `${v}`}
              onChange={setRounds}
            />
            <Slider
              label="Commission they take"
              value={commission}
              min={5}
              max={40}
              format={(v) => `${v}%`}
              onChange={setCommission}
            />
          </>
        )}

        <Slider
          label="Your average green fee"
          value={fee}
          min={20}
          max={120}
          format={(v) => `$${v}`}
          onChange={setFee}
        />

        {!compact && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }} role="group" aria-label="MyGolfLinks plan">
            <span style={{ fontSize: '0.88rem', fontWeight: 600, marginRight: 4 }}>Compare against the</span>
            {[197, 495].map((p) => (
              <button
                key={p}
                type="button"
                className="btn btn-line btn-sm"
                aria-pressed={plan === p}
                style={plan === p ? { background: 'var(--pine)', color: 'var(--cream)', borderColor: 'var(--pine)' } : undefined}
                onClick={() => setPlan(p)}
              >
                ${p} plan
              </button>
            ))}
          </div>
        )}

        {model === 'barter' && (
          <p style={{ fontSize: '0.85rem', color: 'var(--mut)', marginTop: 18, maxWidth: '48ch' }}>
            A common barter contract trades one prime-time foursome a day. That is about 120 rounds
            a month. Set the slider to whatever your contract actually takes.
          </p>
        )}
      </div>

      <div
        className="on-pine"
        style={{ background: 'var(--pine-deep)', borderRadius: 14, padding: 'clamp(24px, 3vw, 36px)', color: 'var(--cream)' }}
      >
        <div style={{ display: 'grid', gap: 16 }}>
          <div>
            <div style={{ fontSize: '0.82rem', color: 'var(--cream-faint)' }}>
              {model === 'barter' ? 'Green-fee value you hand over each year' : 'Commission you pay each year'}
            </div>
            <div className="mono" style={{ fontSize: '1.5rem', fontWeight: 600 }}>{fmtMoney(theirs)}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.82rem', color: 'var(--cream-faint)' }}>
              MyGolfLinks at the same booked volume, plan plus booking fees
            </div>
            <div className="mono" style={{ fontSize: '1.5rem', fontWeight: 600 }}>{fmtMoney(mgl)}</div>
          </div>
          <div style={{ borderTop: '1px solid var(--cream-line)', paddingTop: 16 }}>
            {delta >= 0 ? (
              <>
                <div style={{ fontSize: '0.82rem', color: 'var(--cream-faint)' }}>Stays at your course every year</div>
                <div className="mono" style={{ fontSize: 'clamp(2rem, 4vw, 2.9rem)', fontWeight: 600, color: 'var(--gold-bright)', lineHeight: 1.05 }}>
                  {fmtMoney(delta)}
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: '0.82rem', color: 'var(--cream-faint)' }}>
                  At these numbers the flat plan costs more than your current deal
                </div>
                <div className="mono" style={{ fontSize: '1.6rem', fontWeight: 600 }}>{fmtMoney(-delta)}</div>
                <p style={{ fontSize: '0.82rem', color: 'var(--cream-mut)', marginTop: 8 }}>
                  We would rather show you that than hide it. Honest math is the whole point.
                </p>
              </>
            )}
          </div>
        </div>

        <details className="how" style={{ marginTop: 22, background: 'transparent', borderColor: 'var(--cream-line)' }}>
          <summary style={{ color: 'var(--cream)' }}>How this is calculated</summary>
          <div className="how-body">
            <div className="how-line" style={{ color: 'var(--cream-mut)' }}>{theirsLine}</div>
            <div className="how-line" style={{ color: 'var(--cream-mut)' }}>{mglLine}</div>
            <p style={{ fontSize: '0.8rem', color: 'var(--cream-faint)', marginTop: 8 }}>
              Barter math values traded inventory at your own green fee. The MyGolfLinks side is
              the monthly plan plus the flat $1.80 fee on every app booking, counted against
              MyGolfLinks here to keep the comparison conservative. Flash Deal and point of sale
              fees are finalized at launch and not included here.
            </p>
          </div>
        </details>

        {compact && (
          <p style={{ marginTop: 18, fontSize: '0.88rem' }}>
            <Link to="/why" className="tlink">Run the full version with your own model</Link>
          </p>
        )}
      </div>
    </div>
  );
}
